<template>
  <div v-on-clickaway="closeDropdown" class="personal-counter">
    <EButton
      ref="toggle"
      v-focus="dropdownOpen"
      class="personal-counter__button"
      data-test-id="personal-counter-toggle"
      @click="toggleDropdown"
    >
      <div class="personal-counter__wrapper">
        <div class="personal-counter__content">
          <EPersonalCounterIndicator
            v-if="showIndicator"
            :type="indicatorType"
            data-test-id="personal-counter-indicator"
          />
          <EIcon
            icon="tree-color"
            data-test-id="personal-counter-tree"
          />
          <span
            class="personal-counter__text"
            data-test-id="personal-counter-text"
          >
            {{ trees }}
          </span>
        </div>
        <component
          :is="progressComponent"
          v-if="showProgress"
          :percentage="nextTree"
          :class="`personal-counter__progress--${isDesktopUp ? 'linear' : 'circle' }`"
          data-test-id="personal-counter-progress"
        />
        <EIcon
          v-if="checkpoint"
          :icon="checkpointIcon"
          size="s"
          class="personal-counter__checkpoint"
          data-test-id="personal-counter-checkpoint"
        />
      </div>
    </EButton>

    <EPersonalCounterDropdown
      :trees="trees"
      :visible="dropdownOpen"
      class="personal-counter__dropdown"
      data-test-id="personal-counter-dropdown"
    />
    <EPersonalCounterTooltip
      v-if="checkpoint && tooltipOpen"
      class="personal-counter__tooltip"
      data-test-id="personal-counter-tooltip"
      @close="closeTooltip"
    >
      {{ checkpointText }}
    </EPersonalCounterTooltip>
    <EPersonalCounterModal
      :active="modalOpen"
      data-test-id="personal-counter-modal"
      @close="closeModal"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import { mixin as focus } from 'vue-focus';

import {
  PERSONAL_COUNTER_OVERLAY_DISMISSED as onboardingStorageKey,
  PERSONAL_COUNTER_TOOLTIP_SEARCH as tooltipSearchStorageKey,
  PERSONAL_COUNTER_TOOLTIP_TREE as tooltipTreeStorageKey,
} from '~~/common/js/constants/storage-keys';
import { sendEvent, sendCoreAnalyticsEvent } from '~~/common/js/client/analytics';
import screen from '~~/common/vue/mixins/screen';
import EButton from '~~/common/vue/components/button';
import EIcon from '~~/common/vue/components/icon';
import EPersonalCounterDropdown from './dropdown';
import EPersonalCounterIndicator from './indicator';
import EPersonalCounterModal from './modal';
import EPersonalCounterProgressCircle from './progress-circle';
import EPersonalCounterProgressLinear from './progress-linear';
import EPersonalCounterTooltip from './tooltip';

export default {
  name: 'ENewPersonalCounter',
  components: {
    EButton,
    EIcon,
    EPersonalCounterDropdown,
    EPersonalCounterIndicator,
    EPersonalCounterModal,
    EPersonalCounterProgressCircle,
    EPersonalCounterProgressLinear,
    EPersonalCounterTooltip,
  },
  mixins: [
    clickaway,
    focus,
    screen(),
  ],
  props: {
    shouldIncrement: {
      type: Boolean,
      default: false,
    },
  },
  constants: {
    checkpoints: {
      FIRST_SEARCH: 'firstsearch',
      TREE_PLANTED: 'treeplanted',
    },
    minimumTreeCount: 2,
    // we want the checkpoint to be true in a new search
    // but only if the counter has been recently updated
    searchThreshold: 5000,
  },
  data() {
    return {
      dropdownOpen: false,
      modalOpen: false,
      tooltipOpen: true,
      userOnboarded: true,
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('user', [
      'lastTree',
      'loggedIn',
      'treeCount',
    ]),
    ...mapGetters('user', [
      'nextTree',
      'treesPlanted',
    ]),
    trees() {
      return this.treesPlanted.toLocaleString(this.locale);
    },
    checkpoint() {
      if (!this.shouldIncrement || this.showIndicator) {
        return null;
      }

      const lastIncrement = Math.abs(Date.now() - this.lastTree);
      const incrementSafePeriod = lastIncrement <= this.searchThreshold;
      if (incrementSafePeriod) {
        if (this.treeCount === 1) {
          return this.checkpoints.FIRST_SEARCH;
        }
        if (this.nextTree === 0 && this.treesPlanted > 0) {
          return this.checkpoints.TREE_PLANTED;
        }
      }

      return null;
    },
    checkpointIcon() {
      return this.checkpoint === this.checkpoints.FIRST_SEARCH ? 'leaf' : 'plus-one';
    },
    checkpointStorageKey() {
      if (this.checkpoint === this.checkpoints.FIRST_SEARCH) {
        return tooltipSearchStorageKey;
      }
      if (this.checkpoint === this.checkpoints.TREE_PLANTED) {
        return tooltipTreeStorageKey;
      }
      return null;
    },
    checkpointText() {
      return this.checkpoint ? this.$t(`common.header.counter.action.${this.checkpoint}`) : '';
    },
    displayTrackId() {
      const suffix = this.loggedIn ? 'loggedin' : 'loggedout';
      return `personal-counter-content--${suffix}`;
    },
    progressComponent() {
      return this.isDesktopUp ? 'EPersonalCounterProgressLinear' : 'EPersonalCounterProgressCircle';
    },
    showIndicator() {
      const isExistingUser = this.treeCount >= this.minimumTreeCount;
      return isExistingUser && !this.userOnboarded;
    },
    indicatorType() {
      return this.isDesktopUp ? 'badge' : 'icon';
    },
    showProgress() {
      return !(this.showIndicator && this.isDesktopUp);
    },
  },
  mounted() {
    this.trackDropdownDisplay();
    if (localStorage) {
      this.userOnboarded = !!localStorage.getItem(onboardingStorageKey);
      this.tooltipOpen = !localStorage.getItem(this.checkpointStorageKey);
    }
  },
  methods: {
    closeDropdown({ target }) {
      const { $el } = this.$refs.toggle;
      if (target !== $el && !$el.contains(target)) {
        this.dropdownOpen = false;
      }
    },
    closeModal() {
      this.modalOpen = false;
      localStorage.setItem(onboardingStorageKey, '1');
      this.userOnboarded = true;
    },
    closeTooltip() {
      this.tooltipOpen = false;
      if (this.checkpointStorageKey) {
        localStorage.setItem(this.checkpointStorageKey, '1');
      }
    },
    toggleDropdown() {
      if (this.tooltipOpen && this.checkpoint) {
        return;
      }
      if (this.showIndicator) {
        this.modalOpen = true;
      } else {
        this.dropdownOpen = !this.dropdownOpen;
        this.trackDropdownDisplay();
      }
      const payload = { onboarded: Number(this.userOnboarded).toString() };
      sendEvent('personal-counter-pill', payload);
      sendCoreAnalyticsEvent('personalCounterPillClick', payload);
    },
    trackDropdownDisplay() {
      if (this.dropdownOpen) {
        sendEvent(this.displayTrackId);
        sendCoreAnalyticsEvent('personalCounterContentDisplay', { loggedIn: this.loggedIn });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$button-size: 48px;
$checkpoint-size: $icon-size-s;

.personal-counter {
  position: relative;
}

.personal-counter__button {
  color: var(--color-brand-primary);

  @include desktop-down {
    padding: $space-2s;
    height: $button-size;
    width: $button-size;
  }
}

.personal-counter__wrapper {
  display: flex;
  flex-direction: column;
  gap: $space-2s;
}

.personal-counter__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 0 $space-1s;
  margin-top: $space-1s - $space-3s;

  @include desktop {
    flex-direction: row;
    margin-top: 0;
  }
}

.personal-counter__text {
  color: var(--color-text-primary);
  display: contents;
  font-size: $font-s;

  @include desktop {
    font-size: $font-m;
  }
}

.personal-counter__checkpoint {
  animation: e-checkpoint $timing-1l ease-in-out $timing-m 1;
  height: $checkpoint-size;
  width: $checkpoint-size;
  position: absolute;
  bottom: $space-2l;
  left: $space-2l;
  opacity: 0;
  transform: translateY($checkpoint-size);

  @include desktop {
    bottom: -$space-2s;
    left: calc(50% - #{$checkpoint-size} / 2);
  }
}

.personal-counter__progress--circle {
  position: absolute;
  top: $space-3s;
  left: $space-3s;
}

.personal-counter__progress--linear {
  transform: translateY($space-3s);
}

.personal-counter__dropdown {
  margin-top: $space-s;
  right: $space-s;

  @include tablet {
    @include elevation(2);
  }

  @include desktop {
    left: unset;
    right: -$space-s;
  }
}

.personal-counter__tooltip {
  position: absolute;
  top: $button-size + $space-s;
  left: 0;

  @include desktop {
    left: unset;
    right: $space-l;
  }
}

@keyframes e-checkpoint {
  20% {
    opacity: 1;
    transform: translateY(0);
  }

  80% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
